import { Button, Grid } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { Datepicker } from 'components/reusable/datepicker';
import { ReusableSelect as Select } from 'components/reusable/select';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { adminUsageReport, getAccounts } from 'store/sagas/sagas';
import { isValidDate } from 'utils/validation';
const initialData = {
    from: null,
    to: null,
    accountId: ''
};
const normalizeDate = (date) => {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};
export const AdminUsageReports = () => {
    const { state } = useGlobalState();
    const { asyncDispatch, dispatch } = useGlobalState();
    const [accounts, setAccounts] = useState([]);
    const [filter, setFilter] = useState(initialData);
    useEffect(() => {
        getAccounts(state.accessToken, null)
            .then(({ accounts: newAccounts }) => {
            setAccounts(newAccounts);
        })
            .catch(() => {
            setAccounts([]);
        });
    }, []);
    const updateFilter = (newFilter) => {
        setFilter({ ...initialData, ...newFilter });
    };
    const setDatesCurrentMonth = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth());
        const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        setDateFilter(from, to);
    };
    const setDatesLastMonth = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth() - 1);
        const to = new Date(now.getFullYear(), now.getMonth());
        to.setDate(to.getDate() - 1);
        setDateFilter(from, to);
    };
    const setDateFilter = (from, to) => {
        updateFilter({
            ...filter,
            from,
            to
        });
    };
    const handleDateChange = (date, prop) => {
        updateFilter({
            ...filter,
            [prop]: date && isValidDate(date) ? date : null
        });
    };
    const handleChangeFilters = (e) => {
        updateFilter({
            ...filter,
            [e.target.name]: e.target.value
        });
    };
    const downloadUsageReport = () => {
        if (!filter.from || !filter.to) {
            dispatch(setMessages({
                value: 'Please select a date range',
                severity: 'error'
            }));
            return;
        }
        asyncDispatch(adminUsageReport(normalizeDate(filter.from), normalizeDate(filter.to), filter.accountId))
            .then((res) => {
            const usageReportCsv = convertUsageReportJsonToCsv(res);
            const filename = `Billing Usage Report from ${res.startDate} to ${res.endDate}.csv`;
            // Create a blob with the data we want to download as a file
            const blob = new Blob([usageReportCsv], { type: 'text/csv' });
            // Create an anchor element and dispatch a click event on it to trigger a download
            const a = document.createElement('a');
            a.download = filename;
            a.href = window.URL.createObjectURL(blob);
            const clickEvt = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true
            });
            a.dispatchEvent(clickEvt);
            a.remove();
        })
            .catch(console.error);
    };
    // ordered list of the database columns to include in the report (must match properties in the JSON result)
    const reportColumns = `id, sid, subscription_id, name, total_jobs, idv_job_past_init, crosscheck_job_past_init,
            reverification_job_past_init, idv_billable, crosscheck_billable, darkweb_billable,
            ip_address_billable, physical_address_billable, aml_billable, idv_included, crosscheck_included,
            darkweb_included, ip_address_included, physical_address_included, aml_included,
            aml_job_past_init, reverification_billable, reverification_included, ssn_ecbsv_billable,
            ssn_private_billable, dob_billable, dlv_included, dlv_processed, dlv_billable, dlv_samba, dlv_aamva, selfie_verification_billable, tin_verification_billable, has_active_billing`;
    const columnNames = reportColumns.split(',').map((item) => item.trim());
    const convertUsageReportJsonToCsv = (usageReportJson) => {
        const csvData = [columnNames];
        for (const row of usageReportJson.data) {
            const rowData = [];
            for (const columnName of columnNames) {
                rowData.push(`"${row[columnName] ?? ''}"`);
            }
            csvData.push(rowData);
        }
        return csvData.map((e) => e.join(',')).join('\n');
    };
    return (React.createElement("div", null,
        React.createElement("h1", null, "Usage Reports"),
        React.createElement("div", { className: "filters-container" },
            React.createElement(Grid, { container: true },
                React.createElement(Grid, { container: true, item: true, spacing: 3, xs: 12 },
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Datepicker, { className: "g-full-width", date: filter.from, label: "From", maxDate: filter.to ?? undefined, onChange: (date) => {
                                handleDateChange(date, 'from');
                            } })),
                    React.createElement(Grid, { item: true, xs: 3 },
                        React.createElement(Datepicker, { className: "g-full-width", date: filter.to, label: "To", minDate: filter.from ?? undefined, onChange: (date) => {
                                handleDateChange(date, 'to');
                            } })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Select, { className: "g-input-width-16", items: accounts, label: "Account", name: "accountId", onChange: handleChangeFilters, value: filter.accountId })),
                    React.createElement(Grid, { item: true, xs: 6 },
                        React.createElement("p", null, "Leave account blank to view usage for all accounts, or select an account to view usage for each of that account's groups."),
                        React.createElement("p", null,
                            "Please note that all dates are interpreted as",
                            ' ',
                            React.createElement("b", null,
                                React.createElement("a", { href: "https://www.timeanddate.com/worldclock/timezone/utc", rel: "noreferrer" }, "UTC")),
                            ".")),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Button, { onClick: downloadUsageReport }, "Download")),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement("h3", null, "Initialize Dates:")),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Button, { onClick: setDatesCurrentMonth }, "Current Month")),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Button, { onClick: setDatesLastMonth }, "Previous Month")))))));
};
